import React, { useState, useEffect } from 'react';
import { Layout, Menu, Spin, Button, Typography, Grid } from 'antd';
import {
  UserOutlined, TeamOutlined, ShopOutlined, KeyOutlined,
  BgColorsOutlined, PrinterOutlined, CreditCardOutlined,
  ArrowLeftOutlined, ToolOutlined, EnvironmentOutlined, TagsOutlined
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import LayoutHome from '../../components/layout/LayoutHome';
import { useAuth } from '../../aws/AuthContext';
import { useConfig } from '../../context/ConfigContext';

// Secciones
import SeccionPerfil from './sections/SeccionPerfil';
import SeccionEmpresa from './sections/SeccionEmpresa';
import SeccionImpresion from './sections/SeccionImpresion';
import SeccionRoles from './sections/SeccionRoles';
import SeccionUsuarios from './sections/SeccionUsuarios';
import SeccionPersonalizar from './sections/SeccionPersonalizar';
import SeccionSuscripcion from './sections/SeccionSuscripcion';
import SeccionServicios from './sections/SeccionServicios';
import { usePermission } from '../../hooks/usePermission';
import ModalGestionZonas from '../clientes/modals/ModalGestionZonas';
import SeccionZonas from './sections/SeccionZonas';
import SeccionCategoriasClientes from './sections/SeccionCategoriasClientes';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const Ajustes = () => {

  const { hasPermission } = usePermission();
  const { session, updateUser, loading: authLoading } = useAuth();
  const { config, loading: configLoading, updateEmpresa, updateConfig } = useConfig();
  const location = useLocation();
  const screens = useBreakpoint();

  const [currentMenu, setCurrentMenu] = useState('perfil');
  // Estado para controlar qué ver en móvil: 'menu' o 'content'
  const [mobileView, setMobileView] = useState('menu');
  const [isModalZonasOpen, setIsModalZonasOpen] = useState(false);

  const isMobile = !screens.md;

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');
    if (tab) {
      setCurrentMenu(tab);
      if (isMobile) setMobileView('content');
    }
  }, [location, isMobile]);

  if (authLoading || configLoading || !session) {
    return (
      <LayoutHome pageSeleccionada="ajustes">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Spin size="large" tip="Sincronizando con Yugonub..." />
        </div>
      </LayoutHome>
    );
  }

  const menuItems = [
    {
      type: 'group', label: 'Personal', children: [
        { key: 'perfil', icon: <UserOutlined />, label: 'Configuración Personal' }
      ]
    },
    hasPermission('configuracion:ver_administracion') ? {
      type: 'group', label: 'Administración', children: [
        { key: 'empresa', icon: <ShopOutlined />, label: 'Configuración Empresa' },
        { key: 'suscripcion', icon: <CreditCardOutlined />, label: 'Suscripción y Pagos' },
        { key: 'categorias', icon: <TagsOutlined />, label: 'Tipos de Cliente' }
      ]
    } : null,
    hasPermission('configuracion:ver_seguridad') ? {
      type: 'group', label: 'Seguridad', children: [
        { key: 'usuarios', icon: <TeamOutlined />, label: 'Usuarios' },
        { key: 'roles', icon: <KeyOutlined />, label: 'Roles y Permisos' }
      ]
    } : null,
    hasPermission('configuracion:ver_punto_venta') ? {
      type: 'group', label: 'Punto de Venta', children: [
        { key: 'impresion', icon: <PrinterOutlined />, label: 'Impresión' }
      ]
    } : null,
    ((config.modulo_servicios_activo === true || config.modulo_servicios_activo === 'true') || 
     (config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true')) ? {
      type: 'group', label: 'Módulos Adicionales', children: [
        (config.modulo_servicios_activo === true || config.modulo_servicios_activo === 'true') && hasPermission('configuracion:ver_servicios') ? {
          key: 'servicios', icon: <ToolOutlined />, label: 'Catálogo de Servicios'
        } : null,
        (config.modulo_pedidos_activo === true || config.modulo_pedidos_activo === 'true') ? {
          key: 'zonas', icon: <EnvironmentOutlined />, label: 'Zonas y Rutas'
        } : null
      ].filter(Boolean)
    } : null,
    hasPermission('configuracion:ver_personalizacion') ? {
      type: 'group', label: 'Personalización', children: [
        { key: 'personalizar', icon: <BgColorsOutlined />, label: 'Módulos y Colores' }
      ]
    } : null
  ].filter(Boolean);

  const handleMenuClick = (e) => {
    setCurrentMenu(e.key);
    if (isMobile) setMobileView('content');
  };

  return (
    <LayoutHome pageSeleccionada="ajustes">
      <Layout style={{
        background: '#fff',
        height: isMobile ? 'calc(100vh - 65px)' : 'calc(100vh - 85px)',
        overflow: 'hidden',
        flexDirection: isMobile ? 'column' : 'row'
      }}>

        {/* SIDER / MENU PRINCIPAL */}
        {(!isMobile || (isMobile && mobileView === 'menu')) && (
          <Sider
            width={isMobile ? '100%' : 250}
            theme="light"
            style={{
              borderRight: isMobile ? 'none' : '1px solid #f0f0f0',
              height: '100%',
              overflowY: 'auto',
              backgroundColor: '#fff'
            }}
          >
            {isMobile && (
              <div style={{ margin: '10px 0px  4px 5px' }}>
                <Title level={4}>Configuración</Title>
              </div>
            )}
            <Menu
              mode="inline"
              selectedKeys={[currentMenu]}
              onClick={handleMenuClick}
              items={menuItems}
              style={{ borderRight: 'none', paddingBottom: '40px' }}
            />
          </Sider>
        )}

        {/* CONTENIDO DE LAS SECCIONES */}
        {(!isMobile || (isMobile && mobileView === 'content')) && (
          <Content style={{
            padding: isMobile ? '16px' : '10px 16px',
            maxWidth: '1200px',
            height: '100%',
            overflowY: 'auto',
            backgroundColor: isMobile ? '#fff' : '#fbfbfb',
            transition: 'all 0.3s ease'
          }}>
            {/* Cabecera Móvil para volver atrás */}
            {isMobile && (
              <div style={{ marginBottom: '20px' }}>
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setMobileView('menu')}
                  style={{ padding: 0, marginBottom: '8px' }}
                >
                  Volver a Ajustes
                </Button>
                <Title level={3} style={{ margin: 0 }}>
                  {menuItems.flatMap(g => g.children).find(i => i.key === currentMenu)?.label}
                </Title>
              </div>
            )}

            <div className="fade-in">
              {currentMenu === 'perfil' && <SeccionPerfil session={session} updateUser={updateUser} />}
              {currentMenu === 'empresa' && <SeccionEmpresa config={config} configLoading={configLoading} updateConfig={updateConfig} updateEmpresa={updateEmpresa} />}
              {currentMenu === 'usuarios' && <SeccionUsuarios />}
              {currentMenu === 'roles' && <SeccionRoles />}
              {currentMenu === 'impresion' && <SeccionImpresion config={config} configLoading={configLoading} updateConfig={updateConfig} />}
              {currentMenu === 'servicios' && <SeccionServicios />}
              {currentMenu === 'personalizar' && <SeccionPersonalizar />}
              {currentMenu === 'suscripcion' && <SeccionSuscripcion session={session} config={config} />}
              {currentMenu === 'zonas' && <SeccionZonas />}
              {currentMenu === 'categorias' && <SeccionCategoriasClientes />}
            </div>
          </Content>
        )}
      </Layout>

      {/* MODAL GESTIÓN DE ZONAS */}
      <ModalGestionZonas
        isOpen={isModalZonasOpen}
        setIsOpen={setIsModalZonasOpen}
      />
    </LayoutHome>
  );
};

export default Ajustes;